import React from 'react';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { T } from './declarationTheme';

/* ─────────────────────────────────────────────────────────────────────────
   Primitives visuelles des étapes « Parties adverses » et « Victimes ».

   Parti pris : une carte par bloc, en-tête vert *pâle* (pas de bandeau
   saturé, pas de filet coloré à gauche), coins arrondis, ombre très douce.
   L'accent vert ne sert qu'à l'action principale.
   ───────────────────────────────────────────────────────────────────────── */

/* Sur une page blanche, un liseré à 1px de la couleur des filets internes est
   invisible : la carte doit se détacher par un bord plus franc ET une ombre
   portée. Ces deux valeurs ne sont donc pas prises dans les tokens de filet. */
const CARD_BORDER = '#d7dce5';
const CARD_SHADOW = '0 1px 2px rgba(16,24,40,0.05), 0 14px 28px -20px rgba(16,24,40,0.45)';
const ROW_BG = '#fbfcfd';
const ROW_BORDER = '#e2e6ee';

export { CARD_BORDER, CARD_SHADOW, ROW_BG, ROW_BORDER };

export function Panel({ icon, title, subtitle, count, action, children, style = {} }) {
  return (
    <section
      style={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 14,
        background: T.white,
        boxShadow: CARD_SHADOW,
        overflow: 'hidden',
        ...style,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
          padding: '14px 18px',
          background: T.brandSoft,
          borderBottom: `1px solid #b9d6c5`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
          {icon && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 9,
                background: T.white,
                border: `1px solid ${T.brandLine}`,
                color: T.brand,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {icon}
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: T.brandDark,
                letterSpacing: '-0.005em',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {title}
              {count != null && <CountPill value={count} />}
            </div>
            {subtitle && (
              <div style={{ fontSize: 12, color: T.inkMuted, marginTop: 2 }}>{subtitle}</div>
            )}
          </div>
        </div>
        {action}
      </header>

      <div style={{ padding: '18px' }}>{children}</div>
    </section>
  );
}

export function CountPill({ value, tone = 'brand' }) {
  const palette = tone === 'danger'
    ? { bg: T.dangerSoft, color: T.danger, line: '#fecaca' }
    : { bg: T.white, color: T.brand, line: T.brandLine };
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: '18px',
        minWidth: 20,
        padding: '0 7px',
        borderRadius: 10,
        textAlign: 'center',
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.line}`,
      }}
    >
      {value}
    </span>
  );
}

/** Bouton d'action principal du panneau (dans l'en-tête pâle). */
export function PanelAction({ children, onClick, icon = <PlusOutlined /> }) {
  return (
    <Button
      icon={icon}
      onClick={onClick}
      style={{
        height: 34,
        borderRadius: 9,
        background: T.white,
        borderColor: T.brandLine,
        color: T.brand,
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      {children}
    </Button>
  );
}

/** État vide : illustration discrète, une phrase, une action.
 *
 * La prop s'appelle `icone` et porte un élément React, non plus un émoji : le nom
 * suit le type, faute de quoi un appelant écrirait un caractère là où l'écran
 * attend un élément - et personne ne le verrait avant l'exécution. */
export function EmptyState({ icone, label, actionLabel, onAction }) {
  return (
    <div
      style={{
        padding: '34px 20px 30px',
        textAlign: 'center',
        background: ROW_BG,
        border: `1px dashed ${T.inkGhost}`,
        borderRadius: 11,
      }}
    >
      <div
        style={{
          width: 58,
          height: 58,
          margin: '0 auto 14px',
          borderRadius: '50%',
          background: T.white,
          border: `1px solid ${ROW_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          color: T.inkMuted,
        }}
      >
        {icone}
      </div>
      <div style={{ fontSize: 13.5, color: T.inkMuted, marginBottom: 16 }}>{label}</div>
      {onAction && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAction}
          style={{
            height: 38,
            padding: '0 20px',
            borderRadius: 9,
            background: T.brand,
            borderColor: T.brand,
            fontWeight: 600,
          }}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

/** Ligne de synthèse discrète en pied de panneau. */
export function PanelFooterNote({ tone = 'neutral', children }) {
  const palette = {
    neutral: { bg: T.paper, color: T.inkMuted, line: T.line },
    ok: { bg: T.brandSoft, color: T.brandDark, line: T.brandLine },
    warn: { bg: T.warnSoft, color: T.warn, line: '#fed7aa' },
    danger: { bg: T.dangerSoft, color: T.danger, line: '#fecaca' },
  }[tone];
  return (
    <div
      style={{
        marginTop: 14,
        padding: '10px 14px',
        borderRadius: 10,
        fontSize: 12.5,
        lineHeight: 1.5,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.line}`,
      }}
    >
      {children}
    </div>
  );
}
